'use client';

import { useState } from 'react';
import { Mail, ArrowRight, Check, Loader2, Sparkles, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { signUpForWaitlist } from '@/services/supabase/waitlist';

const WaitlistSignup = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');

    try {
      await signUpForWaitlist({
        email,
        source: 'landing_page',
        interests: ['launch_notification', 'feature_updates', 'founding_member'],
      });

      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      if (err instanceof Error) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <section id="waitlist" className="relative py-24 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.03] to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8">
        <div
          className={cn(
            "relative p-8 md:p-12 rounded-3xl",
            "bg-white/[0.02] backdrop-blur-sm",
            "border border-white/[0.08]",
            "shadow-[0_0_60px_-20px_rgba(59,130,246,0.15)]"
          )}
        >
          {/* Decorative elements */}
          <div className="absolute -top-px left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />
          <div className="absolute -bottom-px left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />

          <div className="text-center mb-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
              <Sparkles className="w-4 h-4 text-primary-400" />
              <span className="text-primary-400 text-sm font-medium">Coming Soon</span>
            </div>

            {/* Headline */}
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-secondary-100 mb-4">
              Be First to Experience{' '}
              <span className="bg-gradient-to-r from-primary-400 to-cyan-400 bg-clip-text text-transparent">
                Axis
              </span>
            </h2>

            {/* Description */}
            <p className="text-secondary-400 text-lg max-w-2xl mx-auto leading-relaxed">
              Get early access when we launch, plus exclusive updates on new features
              and special pricing for founding members.
            </p>
          </div>

          {/* Benefits list */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {[
              { icon: Bell, text: 'Launch notification' },
              { icon: Sparkles, text: 'New feature updates' },
              { icon: Check, text: 'Founding member pricing' },
            ].map((benefit, i) => (
              <div key={i} className="flex items-center gap-2 text-secondary-400">
                <benefit.icon className="w-4 h-4 text-primary-400" />
                <span className="text-sm">{benefit.text}</span>
              </div>
            ))}
          </div>

          {/* Form */}
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-100 mb-2">You're on the list!</h3>
              <p className="text-secondary-400">
                We'll notify you as soon as Axis is ready for launch.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === 'error') setStatus('idle');
                    }}
                    placeholder="Enter your email"
                    className={cn(
                      "w-full pl-12 pr-4 py-4 rounded-xl",
                      "bg-white/[0.03] border",
                      status === 'error'
                        ? "border-red-500/50 focus:border-red-500"
                        : "border-white/[0.08] focus:border-primary-500/50",
                      "text-secondary-100 placeholder-secondary-500",
                      "focus:outline-none focus:ring-2",
                      status === 'error' ? "focus:ring-red-500/20" : "focus:ring-primary-500/20",
                      "transition-all duration-200"
                    )}
                    disabled={status === 'loading'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className={cn(
                    "group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl",
                    "bg-primary-500 text-white font-semibold",
                    "shadow-[0_0_30px_-10px_rgba(59,130,246,0.5)]",
                    "hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.6)]",
                    "hover:bg-primary-400",
                    "disabled:opacity-60 disabled:cursor-not-allowed",
                    "transition-all duration-300"
                  )}
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Joining...</span>
                    </>
                  ) : (
                    <>
                      <span>Join Waitlist</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </div>

              {status === 'error' && (
                <p className="mt-3 text-red-400 text-sm text-center">{errorMessage}</p>
              )}

              <p className="mt-4 text-center text-secondary-500 text-xs">
                No spam, ever. Unsubscribe anytime.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export { WaitlistSignup };
